import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { Header, HeaderButton } from '../../components/header';
import { AddressesList } from '../../components/addresses-list';
import { Address } from '../../@types/address';
import { DeleteIcon, SplitIcon } from '../../assets/svgs';
import { AlertModal } from '../../components/alert-modal';
import { PromptModal } from '../../components/prompt-modal';
import { AddFAB, FAB_BOTTOM_SPACING } from '../../components/add-fab';
import {
  useDefaultRoute,
  useDeleteAddressesFromDefaultRoute,
  useLinkAddressesToDefaultRoute,
  useMoveAddressesToNewDefaultRoute,
  useSetDefaultRouteOrder,
} from '../../queries/default-routes';
import { ConfirmationModal } from '../../components/confirmation-modal';
import { LinkAddressesModal } from '../../components/link-addresses-modal';

export const DefaultRouteEditorScreen = () => {
  const { routeId = '' } = useParams();

  const [selectedAddresses, setSelectedAddresses] = useState<string[]>([]);
  const [isPromptSplitRouteModalVisible, setPromptSplitRouteModalVisibility] = useState(false);
  const [isConfirmDeleteAddressesModalVisible, setConfirmDeleteAddressesModalVisibility] =
    useState(false);
  const [alertMessage, setAlertMessage] = useState<null | string>(null);
  const [isLinkAddressesModalVisible, setLinkAddressesModalVisibility] = useState(false);

  const { isFetching, data: route, refetch: refetchRouteList } = useDefaultRoute(routeId);
  const { mutate: mutateRouteOrder, isLoading: isUpdating } = useSetDefaultRouteOrder(routeId);
  const { mutate: mutateMoveAddressesToNewRoute, isLoading: isMoving } =
    useMoveAddressesToNewDefaultRoute();
  const { mutate: mutateDeleteAddressesFromRoute, isLoading: isDeleting } =
    useDeleteAddressesFromDefaultRoute();
  const { mutate: mutateLinkAddressesToDefaultRoute, isLoading: isLinking } =
    useLinkAddressesToDefaultRoute(routeId);

  const isLoading = isFetching || isUpdating || isMoving || isDeleting || isLinking;

  const toggleAddressSelect = (address: Address) => {
    const newSelectedAddresses = [...selectedAddresses];

    const itemIndex = newSelectedAddresses.findIndex((selectedItem) => {
      return selectedItem === address.id;
    });

    if (itemIndex >= 0) {
      newSelectedAddresses.splice(itemIndex, 1);
    } else {
      newSelectedAddresses.push(address.id);
    }

    setSelectedAddresses(newSelectedAddresses);
  };

  const handleRouteOrderChanged = (newAddressesOrder: string[]) => {
    mutateRouteOrder(
      {
        addresses: newAddressesOrder,
      },
      {
        onSuccess: () => {
          refetchRouteList();
        },
      },
    );
  };

  const handleSplitClick = () => {
    if (selectedAddresses.length <= 0) {
      setAlertMessage('לא נבחרו כתובות');

      return;
    }

    if (selectedAddresses.length === route?.addresses?.length) {
      setAlertMessage('בחרת את כל הכתובות. אין סיבה לפצל');

      return;
    }

    setPromptSplitRouteModalVisibility(true);
  };

  const handleDeleteClick = () => {
    if (selectedAddresses.length <= 0) {
      setAlertMessage('לא נבחרו כתובות');

      return;
    }

    setConfirmDeleteAddressesModalVisibility(true);
  };

  const handleConfirmDeleteClick = () => {
    setConfirmDeleteAddressesModalVisibility(false);

    mutateDeleteAddressesFromRoute(
      {
        routeId,
        addressesList: selectedAddresses,
      },
      {
        onSuccess: () => {
          setSelectedAddresses([]);
          refetchRouteList();
        },
      },
    );
  };

  const handleCancelDeleteClick = () => {
    setConfirmDeleteAddressesModalVisibility(false);
  };

  const handleConfirmSplitAlertClick = () => {
    setAlertMessage(null);
  };

  const handleConfirmSplitClick = (newRouteName: string) => {
    setPromptSplitRouteModalVisibility(false);

    mutateMoveAddressesToNewRoute(
      {
        oldRouteId: routeId,
        newRouteName,
        AddressesList: selectedAddresses,
      },
      {
        onSuccess: () => {
          setSelectedAddresses([]);
          refetchRouteList();
        },
      },
    );
  };

  const handleCancelSplitClick = () => {
    setPromptSplitRouteModalVisibility(false);
  };

  const handleLinkAddressesClick = () => {
    setLinkAddressesModalVisibility(true);
  };

  const handleAddAddressesSaveClick = (addressesIds: string[]) => {
    setLinkAddressesModalVisibility(false);
    mutateLinkAddressesToDefaultRoute(
      {
        addresses: addressesIds,
      },
      {
        onSuccess: () => {
          refetchRouteList();
        },
      },
    );
  };

  const handleAddAddressesCancelClick = () => {
    setLinkAddressesModalVisibility(false);
  };

  const renderScreenTitle = () => {
    if (selectedAddresses.length > 0) {
      return `נבחרו: ${selectedAddresses.length}`;
    }

    return 'עריכת כתובות במסלול';
  };

  const renderActionButtons = (): HeaderButton[] | undefined => {
    if (selectedAddresses.length <= 0) {
      return;
    }

    return [
      { title: 'מחיקה', icon: <DeleteIcon />, onclick: handleDeleteClick },
      { title: 'פיצול', icon: <SplitIcon />, onclick: handleSplitClick },
    ];
  };

  return (
    <>
      <Header
        screenTitle={renderScreenTitle()}
        hasBack
        isLoading={isLoading}
        buttons={renderActionButtons()}
      />
      <div style={{ width: '95%', margin: '10px auto 0 auto', paddingBottom: FAB_BOTTOM_SPACING }}>
        {/* link address permission is under edit default routes */}
        {/* no need for create-address permission here */}
        <AddFAB onClick={handleLinkAddressesClick} />

        <AddressesList
          isEditable
          addresses={route?.addresses ?? []}
          onAddressClick={toggleAddressSelect}
          selectedAddresses={selectedAddresses}
          onRouteOderChanged={handleRouteOrderChanged}
        />
      </div>

      {isPromptSplitRouteModalVisible && (
        <PromptModal
          title="פיצול מסלול"
          inputLabel="שם המסלול החדש"
          onSaveClick={handleConfirmSplitClick}
          onCancel={handleCancelSplitClick}
        />
      )}

      <AlertModal isVisible={!!alertMessage} onConfirm={handleConfirmSplitAlertClick}>
        {alertMessage}
      </AlertModal>

      <ConfirmationModal
        isVisible={!!isConfirmDeleteAddressesModalVisible}
        onConfirm={handleConfirmDeleteClick}
        onCancel={handleCancelDeleteClick}
        onClose={handleCancelDeleteClick}
        title="הסרת כתובות מהמסלול"
      >
        בטוח שברצונך להסיר את הכתובות שנבחרו מהמסלול?
      </ConfirmationModal>

      {isLinkAddressesModalVisible && (
        <LinkAddressesModal
          filterAddresses={route?.addresses.map((address) => address.id)}
          onSave={handleAddAddressesSaveClick}
          onCancel={handleAddAddressesCancelClick}
        />
      )}
    </>
  );
};
